/**
 * 로그인 시도 횟수 제한 (단계적 잠금)
 *
 * Phase 0: 5회 실패 → 5분 잠금
 * Phase 1: 4회 실패 → 10분 잠금
 * Phase 2: 3회 실패 → 15분 잠금
 * Phase 3: 2회 실패 → 30분 잠금
 * Phase 4+: 2회 실패 → 30분씩 증가 (60분, 90분, ...)
 */

const STORAGE_KEY = 'login_attempt_state';

const BASE_PHASES = [
    { maxAttempts: 5, lockMinutes: 5 },
    { maxAttempts: 4, lockMinutes: 10 },
    { maxAttempts: 3, lockMinutes: 15 },
    { maxAttempts: 2, lockMinutes: 30 },
];

interface AttemptState {
    failCount: number;
    lockUntil: number; // ms timestamp (0 = 잠금 없음)
    phase: number;
}

export interface AttemptStatus {
    isLocked: boolean;
    lockRemainingSeconds: number;
    attemptsRemaining: number;
    maxAttempts: number;
}

const DEFAULT: AttemptState = { failCount: 0, lockUntil: 0, phase: 0 };

function load(): AttemptState {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? { ...DEFAULT, ...JSON.parse(raw) } : { ...DEFAULT };
    } catch {
        return { ...DEFAULT };
    }
}

function save(s: AttemptState) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

function phaseConfig(phase: number) {
    if (phase < BASE_PHASES.length) {
        return BASE_PHASES[phase];
    }
    const extraPhases = phase - BASE_PHASES.length + 1;
    return { maxAttempts: 2, lockMinutes: 30 + extraPhases * 30 };
}

export const loginAttemptTracker = {
    getStatus(): AttemptStatus {
        const s = load();
        const now = Date.now();
        const isLocked = s.lockUntil > 0 && now < s.lockUntil;
        const lockRemainingSeconds = isLocked ? Math.ceil((s.lockUntil - now) / 1000) : 0;
        const cfg = phaseConfig(s.phase);
        const attemptsRemaining = Math.max(0, cfg.maxAttempts - s.failCount);
        return { isLocked, lockRemainingSeconds, attemptsRemaining, maxAttempts: cfg.maxAttempts };
    },

    recordFailure(): AttemptStatus {
        let s = load();
        const now = Date.now();

        // 잠금 만료됐으면 다음 단계로 진입 (더 짧은 허용 횟수 + 더 긴 잠금)
        if (s.lockUntil > 0 && now >= s.lockUntil) {
            s = { failCount: 0, lockUntil: 0, phase: s.phase + 1 };
        }

        s.failCount += 1;
        const cfg = phaseConfig(s.phase);

        if (s.failCount >= cfg.maxAttempts) {
            s.lockUntil = now + cfg.lockMinutes * 60 * 1000;
        }

        save(s);
        return this.getStatus();
    },

    reset() {
        localStorage.removeItem(STORAGE_KEY);
    },
};
